/**
 * Camlin Companion — persisted "Enable sound" choice (Keith, 6 Aug 2026).
 *
 * Browsers block audio playback until a user gesture (autoplay policy), so
 * the widget shows an explicit labelled "Enable sound" button the first
 * time the panel opens. Once the visitor taps it we persist the choice
 * here; on later visits the widget auto-attempts the technical unlock on
 * the first gesture it gets (the panel-open click), instead of asking
 * again. NOTE this records the visitor's UX preference only — the actual
 * unlock still has to run inside a user gesture on every page load, per
 * browser policy. Absence of a record means "not chosen yet", not
 * "declined": there is no declined state, sound simply stays locked until
 * the visitor taps the button.
 *
 * Same storage contract as `consent.ts` (`CompanionConsentStorage`) so the
 * widget's injectable-storage test seam covers this module too.
 */

import type { CompanionConsentStorage } from './consent';

export const COMPANION_SOUND_CONSENT_STORAGE_KEY = 'camlin-companion-sound-v1';

export interface CompanionSoundConsentRecord {
  choice: 'enabled';
  recordedAt: string;
}

export function readCompanionSoundConsent(
  storage: CompanionConsentStorage
): CompanionSoundConsentRecord | null {
  const raw = storage.getItem(COMPANION_SOUND_CONSENT_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as CompanionSoundConsentRecord;
    if (parsed.choice === 'enabled') {
      return parsed;
    }
  } catch {
    // ignore corrupt storage
  }

  return null;
}

export function writeCompanionSoundConsent(
  storage: CompanionConsentStorage
): CompanionSoundConsentRecord {
  const record: CompanionSoundConsentRecord = {
    choice: 'enabled',
    recordedAt: new Date().toISOString()
  };
  storage.setItem(COMPANION_SOUND_CONSENT_STORAGE_KEY, JSON.stringify(record));
  return record;
}

export function isCompanionSoundEnabled(storage: CompanionConsentStorage): boolean {
  return readCompanionSoundConsent(storage)?.choice === 'enabled';
}

export function clearCompanionSoundConsent(storage: CompanionConsentStorage): void {
  storage.removeItem(COMPANION_SOUND_CONSENT_STORAGE_KEY);
}
